import { FocusDetails } from "@noriginmedia/norigin-spatial-navigation"
import moment from "moment"
import React, { RefObject } from "react"

import { canPlayFlacAudio, dimensions, Platform, platform } from "./constants"
import { getIs4K } from "./device-info"
import { captureSentryException } from "./sentry"
import { canPlayDolbyAtmos } from "./streaming-helpers"

export const secondsToMinutes = (seconds: number) => Math.ceil(seconds / 60)
export const secondsToTime = (seconds: number) => {
  const s = Math.abs(Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s - h * 3600) / 60)
  return `${seconds < 0 ? "-" : ""}${h > 0 ? h.toString().padStart(2, "0") + ":" : ""}${m
    .toString()
    .padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`
}

// this class combines the [get, set] for better readability
type Set<T> = React.Dispatch<React.SetStateAction<T>>
export class comb<T> {
  constructor(
    private get: T,
    private setter: Set<T>,
  ) {}
  get val() {
    return this.get
  }
  get set() {
    return this.setter
  }
  static ine = <P>(g: P, s: Set<P>) => new this(g, s)
}

export const CloudinaryConf = {
  cloud: {
    cloudName: "dg-stage",
  },
  url: {
    cname: "cloudinary://@dg-stage",
  },
}

export const removeSlashes = (s: string) => s.replace(/\//g, "")

export const isTicketValid = (date?: string) => {
  if (!date) return false
  return moment(date) >= moment.utc()
}

export const exitApp = () => {
  if (platform === Platform.WebOS) window.close()
  else if (platform === Platform.Tizen) {
    const referrerWindow = window.opener || window.parent
    if (referrerWindow) {
      // Send an exit signal to the referrer
      referrerWindow.postMessage("exitApp", "*")
    } else {
      console.error("Unable to communicate with the referrer.")
    }
  } else if (platform === Platform.Chromecast) {
    const context = cast.framework.CastReceiverContext.getInstance()
    context.stop()
  } else alert("Platform: Web - Cannot exit!")
}

const removeAndAddCSS = (
  ref: HTMLElement,
  property: string,
  removePattern: string,
) => {
  const args = ref.style.transition
    .split(",")
    .map((item) => item.trim())
    .filter((item) => {
      return item.indexOf(removePattern) === -1
    })
  args.push(property)
  return args.join(", ")
}
export const instantScroll = (
  scrollView: HTMLElement | RefObject<HTMLElement | null>,
  query: FocusDetails,
  transition: string,
) => {
  if (!scrollView) return
  const ref = "current" in scrollView ? scrollView.current : scrollView
  if (!ref) return
  // check if there's anything to do here
  const transform = ref.style.transform
  if (transform.includes(transition)) return

  if (query.instantScroll) {
    ref.style.transition = removeAndAddCSS(ref, "transform 0s", "transform")
    setTimeout(() => {
      if (ref) {
        ref.style.transition = removeAndAddCSS(ref, transition, "transform")
      }
    }, 50)
  }
}

export const hwScroll = ({
  scrollRef,
  y,
  height,
}: {
  scrollRef: RefObject<HTMLElement | null>
  y: number
  height?: number
}) => {
  if (!scrollRef) return
  const h = height ?? dimensions.height
  const scrollAmount = y - h / 2 + 100
  if (scrollRef.current) {
    const bottomBoundary = Math.max(0, scrollRef.current.scrollHeight - h)
    scrollRef.current.style.transform =
      "translate3d(0, " +
      -(scrollAmount < 0
        ? 0
        : scrollAmount > bottomBoundary
          ? bottomBoundary
          : scrollAmount) +
      "px, 0)"
  }
}

export const mouseHoverScroll = ({
  scrollView,
  direction,
  scrollAmount,
  offset = 0,
  setIndex,
}: {
  scrollView?: HTMLElement | null
  direction?: "left" | "right" | null
  scrollAmount: number
  offset?: number
  setIndex?: (index: number) => void
}) => {
  if (typeof WebKitCSSMatrix === "undefined") return
  if (!scrollView) return
  if (direction && scrollView) {
    const style = window.getComputedStyle(scrollView)
    const currentWidth = parseInt(style.width, 10)
    if (currentWidth + offset < dimensions.width) return
    const matrix = new WebKitCSSMatrix(style.transform)
    const currentX = matrix.m41
    let scrollPosition =
      currentX + scrollAmount * (direction === "left" ? 1 : -1)
    if (direction === "left") scrollPosition = Math.min(offset, scrollPosition)
    if (direction === "right")
      scrollPosition =
        Math.min(
          currentWidth + offset - dimensions.width,
          scrollPosition * -1,
        ) * -1
    setIndex?.(-(scrollPosition / scrollAmount))
    scrollView.style.transform = "translate3d(" + scrollPosition + "px, 0, 0)"
  }
}

export const differenceInSeconds = (
  fromDate: Date,
  toDate: Date = new Date(),
): number => {
  return Math.floor((fromDate.valueOf() - toDate.valueOf()) / 1000)
}

export type Duration = {
  days: number
  hours: number
  minutes: number
  seconds: number
}
export const secondsToDuration = (seconds: number): Duration => {
  if (seconds <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 }
  }

  return {
    days: Math.floor(seconds / (3600 * 24)),
    hours: Math.floor((seconds / 3600) % 24),
    minutes: Math.floor((seconds / 60) % 60),
    seconds: Math.floor(seconds % 60),
  }
}

// can't use URLSearchParams for backwards compatibility
export const encodePostData = (body: Record<string, string>) => {
  const formBody = []
  for (const property in body) {
    const encodedKey = encodeURIComponent(property)
    const encodedValue = encodeURIComponent(body[property])
    formBody.push(encodedKey + "=" + encodedValue)
  }
  return formBody.join("&")
}

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.toLowerCase().slice(1)
}

export type Capabilities = {
  flac: boolean
  dolbyAtmos: boolean
  is4K: boolean
}

export const getPlayerCapabilities = async (): Promise<Capabilities> => {
  // don't repeat queries
  if (window.capabilities !== undefined) return window.capabilities
  window.capabilities = { flac: false, dolbyAtmos: false, is4K: false }
  window.capabilities.flac = canPlayFlacAudio
  window.capabilities.dolbyAtmos = canPlayDolbyAtmos()
  try {
    window.capabilities.is4K = await getIs4K()
  } catch (e) {
    captureSentryException(e)
    window.capabilities.is4K = false
  }
  return window.capabilities
}

// scaling variable
export const s = <T extends string | number>(size: T, round = false, f = 1) => {
  const factor = (window.innerWidth / 1920) * f
  return (
    typeof size === "string"
      ? size.replace(/(\d+(\.\d+)?)/g, function (match) {
          const result = parseFloat(match)
          return (
            round ? Math.floor(result * factor) : result * factor
          ).toString()
        })
      : round
        ? Math.floor((size as number) * factor)
        : (size as number) * factor
  ) as T
}

// in order to keep this query synchronous, we will try to query the global variable on 4K
export const ccFactor = (size: number) =>
  platform === Platform.Chromecast || window.capabilities.is4K
    ? Math.floor(size * 2)
    : size

// re-read aria-label
export const reReadAriaLabel = (ref: RefObject<HTMLElement | null>) => {
  setTimeout(() => {
    ref.current?.blur()
    setTimeout(() => ref.current?.focus(), 200)
  }, 10)
}

export const CuratedContentSlugV2 = {
  Audio: "audio",
  Discover: "discover",
  Live: "live",
  Search: "search",
} as const
export type CuratedContentSlugV2 =
  (typeof CuratedContentSlugV2)[keyof typeof CuratedContentSlugV2]

export const mapAudioTrackName = (audioTrackName?: string) => {
  if (!audioTrackName) return undefined
  if (["dolby", "atmos", "dolby atmos"].includes(audioTrackName)) return "atmos"
  if (["lossless", "flac"].includes(audioTrackName)) return "lossless"
  if (["stereohigh", "hd", "stereo high"].includes(audioTrackName)) return "hd"
  return "sd"
}
